/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useRef, useState } from 'react';

// Mantine
import { Box, Drawer, ScrollArea } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

// Styles
import classes from './UpdateSite.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Components : Steps
import { UpdateSiteForm } from './UpdateSiteForm';

// Store
import useStore from '@store/index';
import { convertKWHToMWHValue } from '@utils/functions';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { TFormValuesSite } from '../../types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const UpdateSite = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const location = useLocation();
	const { projectId } = useParams();

	const { setParamsFromProject, sitesSelectedRow } =
		useStore();
	// Validators
	const formSite = useForm<TFormValuesSite>({
		initialValues: {
			project: '',
			organization: '',
			entity: '',
			name: '',
			minValue: 0,
			maxValue: 0,
			countryCode: '',
			place: '',
		},

		validate: {
			countryCode: isNotEmpty(
				t('global.countryRequired')
			),
			entity: isNotEmpty(t('global.entityRequired')),
			organization: isNotEmpty(t('global.siteRequired')),
			project: isNotEmpty(t('global.projectRequired')),
			name: (value) =>
				value.length < 2
					? t('errors.nameMustHaveAtLeast2Letters')
					: null,
			place: (value, values) =>
				values.countryCode !== '' &&
				values.countryCode !== null &&
				value.length < 2
					? t('errors.placeMustHaveAtLeast2Letters')
					: null,
			minValue: (value, values) =>
				value >= values.maxValue &&
				value !== 0 &&
				values.maxValue !== 0
					? t('global.minValueLessThanMaxValue')
					: null,
			maxValue: (value, values) =>
				value <= values.minValue &&
				value !== 0 &&
				values.minValue !== 0
					? t('global.maxValueGreaterThanMaxValue')
					: null,
		},
	});

	useEffect(() => {
		if (!isNil(sitesSelectedRow)) {
			const convertValue = (value?: number) => {
				if (
					sitesSelectedRow?.unit?.toLowerCase() === 'item'
				)
					return value ?? 0;
				return value ? convertKWHToMWHValue(value) : 0;
			};

			formSite.setValues({
				name: sitesSelectedRow.name,
				organization:
					sitesSelectedRow?.organization?.id || '',
				entity: sitesSelectedRow?.entity?.id || '',
				project: sitesSelectedRow?.project?.id || '',
				countryCode:
					sitesSelectedRow?.location?.countryCode || '',
				place: sitesSelectedRow?.location?.place || '',
				maxValue: convertValue(
					sitesSelectedRow?.production?.maxValue
				),
				minValue: convertValue(
					sitesSelectedRow?.production?.minValue
				),
			});

			formSite.resetDirty();
		}
	}, [sitesSelectedRow]);

	const isPreviousMeasurabilityProjectDetails =
		location.pathname.split('/')[1] ===
		'measurability-projects';

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						setParamsFromProject({});
						formSite.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={
						projectId
							? t('sites.backToProjectDetailsSure')
							: t('sites.backToAllSitesSure')
					}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{projectId
							? t('sites.backToProjectDetails')
							: t('sites.backToAllSites')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<UpdateSiteForm
								selectedItem={sitesSelectedRow}
								isPreviousMeasurabilityProjectDetails={
									isPreviousMeasurabilityProjectDetails
								}
								formSite={formSite}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									formSite.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
